/**
 * Authentication Store
 *
 * Manages Learnworlds user authentication and session tokens.
 * Integrates with authService for backend communication.
 *
 * [A009] Implementation
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService, type AuthenticatedUser, AuthenticationError } from '../services/authService'
import {
  extractAuthParams,
  extractAndValidateAuthParams,
  hasAuthParams,
  type LearnworldsAuthParams,
} from '../utils/urlParams'

/**
 * Authenticated user information (store interface)
 */
export interface AuthUser {
  id: string
  email: string
  name?: string
}

/**
 * Session data (store interface)
 */
export interface AuthSession {
  sessionToken: string
  user: AuthUser
  expiresAt: string
}

/**
 * Authentication error details
 */
export interface AuthError {
  message: string
  code: string
  details?: string[]
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const session = ref<AuthSession | null>(null)
  const isAuthenticating = ref(false)
  const authError = ref<AuthError | null>(null)
  const isInitialized = ref(false)

  // Computed
  const isAuthenticated = computed(() => session.value !== null)
  const currentUser = computed(() => session.value?.user || null)
  const sessionToken = computed(() => session.value?.sessionToken || null)

  /**
   * Check if session is expired
   */
  const isSessionExpired = computed(() => {
    if (!session.value) return true

    const expiresAt = new Date(session.value.expiresAt)
    return expiresAt < new Date()
  })

  // Methods

  /**
   * Validate authentication with backend
   *
   * @param params - Learnworlds authentication parameters
   * @returns true if authentication successful
   */
  const validateAuth = async (params: LearnworldsAuthParams): Promise<boolean> => {
    try {
      isAuthenticating.value = true
      authError.value = null

      console.log('[Auth Store] Validating authentication...')

      // Call auth service to validate with backend
      const { token, user, expiresAt } = await authService.validateAuth(params)

      // Store session data
      session.value = {
        sessionToken: token,
        user: {
          id: user.id,
          email: user.email,
          name: user.name,
        },
        expiresAt: expiresAt.toISOString(),
      }

      console.log('[Auth Store] Authentication successful:', {
        userId: user.id,
        email: user.email,
      })

      isAuthenticating.value = false
      isInitialized.value = true
      return true
    } catch (error) {
      console.error('[Auth Store] Authentication failed:', error)

      if (error instanceof AuthenticationError) {
        authError.value = {
          message: error.message,
          code: error.code,
          details: error.details,
        }
      } else {
        authError.value = {
          message: error instanceof Error ? error.message : 'Authentication failed',
          code: 'UNKNOWN_ERROR',
        }
      }

      isAuthenticating.value = false
      isInitialized.value = true
      return false
    }
  }

  /**
   * Initialize authentication from URL parameters
   *
   * Extracts URL parameters, validates them, and creates session with backend.
   * Should be called when app loads or when user navigates to game page.
   *
   * @returns true if authentication successful
   */
  const initFromUrl = async (): Promise<boolean> => {
    console.log('[Auth Store] Initializing authentication from URL...')

    // Extract and validate URL parameters
    const result = extractAndValidateAuthParams()

    if (!result.success || !result.params) {
      authError.value = {
        message: result.errors?.join(', ') || 'Missing authentication parameters',
        code: 'INVALID_PARAMS',
        details: result.errors,
      }
      isInitialized.value = true
      return false
    }

    // Validate with backend
    return await validateAuth(result.params)
  }

  /**
   * Restore session from local storage
   *
   * Attempts to restore a previously saved session.
   * Validates token with backend before restoring.
   *
   * @returns true if session restored successfully
   */
  const restoreSession = async (): Promise<boolean> => {
    console.log('[Auth Store] Attempting to restore session...')

    try {
      isAuthenticating.value = true
      authError.value = null

      const restored = await authService.restoreSession()

      if (restored) {
        const user = authService.getUser()
        const token = authService.getSessionToken()

        if (user && token) {
          // Restore session in store
          session.value = {
            sessionToken: token,
            user: {
              id: user.id,
              email: user.email,
              name: user.name,
            },
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
          }

          console.log('[Auth Store] Session restored successfully')
          isAuthenticating.value = false
          isInitialized.value = true
          return true
        }
      }

      console.log('[Auth Store] No valid session to restore')
      isAuthenticating.value = false
      isInitialized.value = true
      return false
    } catch (error) {
      console.error('[Auth Store] Session restoration failed:', error)
      isAuthenticating.value = false
      isInitialized.value = true
      return false
    }
  }

  /**
   * Initialize authentication
   *
   * Attempts to restore session from storage, or initialize from URL params.
   * This is the main entry point for authentication initialization.
   *
   * @returns true if authentication successful
   */
  const initialize = async (): Promise<boolean> => {
    if (isInitialized.value) {
      console.log('[Auth Store] Already initialized')
      return isAuthenticated.value
    }

    console.log('[Auth Store] Initializing authentication...')

    // First, try to restore from storage
    const restored = await restoreSession()
    if (restored) {
      return true
    }

    // If no stored session, check for URL parameters
    if (hasAuthParams()) {
      return await initFromUrl()
    }

    // No authentication available
    authError.value = {
      message: 'No authentication parameters found',
      code: 'NO_AUTH',
    }
    isInitialized.value = true
    return false
  }

  /**
   * Logout and clear session
   */
  const logout = async (): Promise<void> => {
    console.log('[Auth Store] Logging out...')

    try {
      await authService.logout()
    } catch (error) {
      console.error('[Auth Store] Logout error:', error)
    } finally {
      session.value = null
      authError.value = null
    }
  }

  /**
   * Clear session (local only, no backend call)
   */
  const clearSession = (): void => {
    console.log('[Auth Store] Clearing session...')
    authService.clearSession()
    session.value = null
    authError.value = null
  }

  /**
   * Clear authentication error
   */
  const clearError = (): void => {
    authError.value = null
  }

  /**
   * Validate current session token with backend
   *
   * @returns true if token is still valid
   */
  const validateToken = async (): Promise<boolean> => {
    const token = sessionToken.value
    if (!token) {
      return false
    }

    try {
      const valid = await authService.validateToken(token)
      if (!valid) {
        clearSession()
      }
      return valid
    } catch (error) {
      console.error('[Auth Store] Token validation failed:', error)
      clearSession()
      return false
    }
  }

  return {
    // State
    session,
    isAuthenticating,
    authError,
    isInitialized,

    // Computed
    isAuthenticated,
    currentUser,
    sessionToken,
    isSessionExpired,

    // Methods
    initialize,
    validateAuth,
    initFromUrl,
    restoreSession,
    logout,
    clearSession,
    clearError,
    validateToken,
  }
})